import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  getPortalToken,
  portalRequestDetail,
  portalSecureRequest,
  portalSecureUpload,
  portalUploadItem,
} from "@/modules/tramites/materialidad/clientPortalApi";
import PortalMaterialidadShell from "./PortalMaterialidadShell";

export default function PortalMaterialidadRequestPage({ mode = "auth" }) {
  const { requestId, token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === "secure") {
        const data = await portalSecureRequest(token);
        setDetail(data);
      } else {
        if (!getPortalToken()) {
          navigate("/portal-materialidad/login", { replace: true });
          return;
        }
        const data = await portalRequestDetail(requestId);
        setDetail(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir la solicitud.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [mode, token, requestId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const onUpload = async (itemId, file) => {
    if (!file) return;
    setUploadingId(itemId);
    try {
      if (mode === "secure") {
        await portalSecureUpload(token, itemId, file);
      } else {
        await portalUploadItem(itemId, file);
      }
      toast.success("Documento cargado.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el archivo.");
    } finally {
      setUploadingId(null);
    }
  };

  const req = detail?.request;

  return (
    <PortalMaterialidadShell>
      <div className="space-y-4" data-testid="portal-materialidad-request">
        {mode === "auth" ? (
          <Link
            to="/portal-materialidad/empresas"
            className="inline-flex items-center gap-1 text-xs text-[#0F2D5C] no-underline"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Mis empresas
          </Link>
        ) : null}

        {loading ? (
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando solicitud…
          </p>
        ) : !req ? (
          <p className="text-sm text-slate-500">Solicitud no disponible.</p>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-semibold text-[#0F2D5C]">{req.title}</h1>
              <p className="text-sm text-slate-600 mt-1">
                {req.client_name} · RFC {req.client_rfc || "—"}
              </p>
              {req.message ? <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{req.message}</p> : null}
              {detail?.disclaimer ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mt-3">
                  {detail.disclaimer}
                </p>
              ) : null}
            </div>

            <ul className="space-y-3">
              {(detail.items || []).map((it) => (
                <li
                  key={it.id}
                  className="rounded-sm border border-slate-200 bg-white px-4 py-3"
                  data-testid={`portal-item-${it.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {it.document_type_label || it.document_type}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Estado: {it.status}
                        {it.filename ? ` · ${it.filename}` : ""}
                      </p>
                      {it.observation ? (
                        <p className="text-xs text-rose-700 mt-1">Observación: {it.observation}</p>
                      ) : null}
                    </div>
                    <div>
                      <label
                        className={`inline-flex items-center gap-1 rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-medium text-white cursor-pointer ${
                          uploadingId === it.id ? "opacity-60 pointer-events-none" : ""
                        }`}
                      >
                        <input
                          type="file"
                          className="sr-only"
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          disabled={uploadingId === it.id}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            onUpload(it.id, f);
                          }}
                          data-testid={`portal-upload-${it.id}`}
                        />
                        {uploadingId === it.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Cargar
                      </label>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </PortalMaterialidadShell>
  );
}
